import { useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { Bar } from 'react-chartjs-2'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import GraphContainer from '../../GraphContainer'
import { hasChartData } from '../helpers/chartDataHelpers'
import Tabs from '../../../../../../features/components/Tabs'

import { useSroiFilters } from '../../../SroiFiltersContext'
import { generateSocialValueByStakeholdersData } from './socialValueByStakeholdersHelpers'
import { useSroiReportCurrencySymbol } from '../../../hooks'
import { useChartExportMode } from '../../../hooks/useChartExportMode'
import { usePdfExport } from '../../../PdfExportContext'
import { useExportAnimationCallback } from '../../../hooks/useExportAnimationCallback'
import { getHtmlLegendPlugin } from '../../../../plugins'
import { getOptions } from './options'
import { calculateHorizontalScrollSettings } from './scrollHelpers'

import { socialReturnOnInvestmentReportSelector } from '../../../../../../modules/selectors/socialReturnOnInvestment'

import { gridCharts, pdfExportComponentKeys, helpTexts } from '../../../constants'
import { tabOptions } from './fields'

import useStyles from './styles'

const SocialValueByStakeholders = ({
  chartMode,
  onChartModeChange,
  onExportStart,
  onExportEnd,
  isExportCopy,
  onChartReady
}) => {
  const { t } = useTranslation()
  const { selectedRegion } = useSroiFilters()

  const report = useSelector(socialReturnOnInvestmentReportSelector)
  const currencySymbol = useSroiReportCurrencySymbol()

  // Track export mode (PDF or PNG) using centralized hook
  const { isExporting, isPdfGenerating } = useChartExportMode()
  const { signalComponentReady } = usePdfExport()

  // Handle animation completion callbacks for export modes (PNG and PDF)
  const handleAnimationComplete = useExportAnimationCallback({
    isExportCopy,
    onChartReady,
    isPdfGenerating,
    signalComponentReady,
    componentKey: pdfExportComponentKeys.stakeholdersChart
  })

  const graphData = useMemo(() => {
    return generateSocialValueByStakeholdersData({ chartMode, selectedRegion, report })
  }, [chartMode, report, selectedRegion])

  // Calculate scroll settings based on number of stakeholder types
  // During export: disable scrolling and let chart fit all bars within container
  const { isScrollable, chartWidth } = useMemo(() => {
    if (isExporting) {
      // During export, disable scrolling and don't set explicit width
      // Chart.js will automatically fit all bars within the container width
      return {
        isScrollable: false,
        chartWidth: null
      }
    }

    const stakeholderCount = graphData?.labels?.length || 0
    return calculateHorizontalScrollSettings({
      itemCount: stakeholderCount,
      maxVisibleItems: 8,
      widthPerItem: 70
    })
  }, [graphData, isExporting])

  const classes = useStyles({ isScrollable, chartWidth })
  const legendContainerId = useMemo(() => uuidv4(), [])

  const options = useMemo(() => {
    const baseOptions = getOptions(legendContainerId, currencySymbol, isScrollable, isExporting)

    // Add animation callback for export modes (PNG or PDF)
    // PNG export: signals to exportChartAsPng via onChartReady
    // PDF export: signals to PdfExportContext via signalComponentReady
    if ((isExportCopy && onChartReady) || (isPdfGenerating && !isExportCopy)) {
      return {
        ...baseOptions,
        animation: {
          ...(baseOptions.animation || {}),
          onComplete: handleAnimationComplete
        }
      }
    }

    return baseOptions
  }, [
    legendContainerId,
    currencySymbol,
    isScrollable,
    isExporting,
    isExportCopy,
    onChartReady,
    isPdfGenerating,
    handleAnimationComplete
  ])

  const plugins = useMemo(() => {
    const htmlLegendPlugin = getHtmlLegendPlugin({
      display: 'flex',
      flexDirection: 'row',
      gap: '8px'
    })
    return [htmlLegendPlugin]
  }, [])

  // Don't render chart if there's no data
  if (!hasChartData(graphData)) {
    return null
  }

  // Use a different ID for the export copy to avoid duplicate IDs in the DOM
  const containerId = isExportCopy
    ? `${gridCharts.sroiSocialValueStakeholders}-export`
    : gridCharts.sroiSocialValueStakeholders

  return (
    <GraphContainer
      id={containerId}
      title={t('Impact by Stakeholders')}
      headingContainerClassName={classes.headingContainer}
      headingChildren={<Tabs options={tabOptions} selectedValue={chartMode} onSelectValue={onChartModeChange} />}
      onExportStart={onExportStart}
      onExportEnd={onExportEnd}
      helpText={helpTexts.socialValueByStakeholders}
    >
      <div className={classes.legendContainer} id={legendContainerId}>
        {/*// The legend of the graph will be rendered here by the plugin*/}
      </div>

      <div className={classes.chartWrapper}>
        <div className={classes.chartCanvas}>
          <Bar type="bar" data={graphData} options={options} plugins={plugins} />
        </div>
      </div>
    </GraphContainer>
  )
}

SocialValueByStakeholders.propTypes = {
  chartMode: PropTypes.string.isRequired,
  onChartModeChange: PropTypes.func.isRequired,
  onExportStart: PropTypes.func,
  onExportEnd: PropTypes.func,
  isExportCopy: PropTypes.bool,
  onChartReady: PropTypes.func
}

export default SocialValueByStakeholders
